<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { LogsDocument, LogType } from '@/gql/graphql';
import { truncateText } from '@/utils/string-utils';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatDateTime, formatDateTimeStringToIso } from '@/utils/date-utils';
import { highlightJson } from '@/utils/prism-utils';
import { PAGING_ITEMS_PER_PAGE, TRUNCATE_TEXT_LENGTH } from '@/constants';

// Filtering and paging state
const organizationId = ref<number | null>(null);
const userId = ref<number | null>(null);
const actionPattern = ref('');
const textPattern = ref('');
const levelPattern = ref('');
const dateFrom = ref<string | null>(null);
const timeFrom = ref<string | null>(null);
const dateTo = ref<string | null>(null);
const timeTo = ref<string | null>(null);
const dateTimeFrom = ref<string | null>(null);
const dateTimeTo = ref<string | null>(null);
const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return logsQueryResult.value?.logs?.total || 0;
});

// Log detail state
const showModalLogDetail = ref(false);
const selectedLog = ref<LogType | null>(null);

// --- Query Logs ---
const theLogs = computed(() => {
  const logList = logsQueryResult.value?.logs?.logs || [];
  return logList
    .filter((log1) => log1 && log1 !== null)
    .map((aLog) => ({
      ...aLog,
      createdAt: aLog?.createdAt ? new Date(aLog.createdAt) : undefined,
      _rowVariant: logLevelVariant(aLog?.level),
    }));
});

const showLogQueryError = computed(() => {
  return logsQueryError.value !== null;
});

const {
  result: logsQueryResult,
  loading: logsQueryInProgress,
  error: logsQueryError,
  refetch: refetchLogs,
} = useQuery(
  LogsDocument,
  {
    organizationId: organizationId.value,
    userId: userId.value,
    actionPattern: actionPattern.value,
    textPattern: textPattern.value,
    levelPattern: levelPattern.value,
    before: dateTimeFrom.value,
    after: dateTimeTo.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  },
  {
    fetchPolicy: 'no-cache',
  }
);

function clearFilter() {
  organizationId.value = null;
  userId.value = null;
  actionPattern.value = '';
  textPattern.value = '';
  levelPattern.value = '';
  dateFrom.value = null;
  timeFrom.value = null;
  dateTo.value = null;
  timeTo.value = null;
  dateTimeFrom.value = null;
  dateTimeTo.value = null;
  pagingPageCurrent.value = 1;

  requestRefetchLogs();
}

function requestRefetchLogs() {
  // check integer values are integer values
  if (!Number.isInteger(organizationId.value)) {
    console.warn('Invalid organization ID. Resetting to null.');
    organizationId.value = null;
  }
  if (!Number.isInteger(userId.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    userId.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dateTimeFrom.value = formatDateTimeStringToIso(
    dateFrom.value,
    timeFrom.value
  );
  dateTimeTo.value = formatDateTimeStringToIso(dateTo.value, timeTo.value);

  // refetch the query
  logsQueryInProgress.value = true;
  logsQueryError.value = null;

  refetchLogs({
    organizationId: organizationId.value,
    userId: userId.value,
    actionPattern: actionPattern.value,
    textPattern: textPattern.value,
    levelPattern: levelPattern.value,
    before: dateTimeTo.value,
    after: dateTimeFrom.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      logsQueryInProgress.value = false;
      logsQueryError.value = err;
      console.error('Refetch failed:', err);
    })
    .then(() => {
      logsQueryInProgress.value = false;
      console.log('refetched');
    });
}

const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: false,
    formatter: formatDateTime,
    tdClass: 'align-middle',
  },
  {
    key: 'organization.name',
    label: 'Organization',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'user.email',
    label: 'User',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'action',
    label: 'Action',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'level',
    label: 'Level',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'text',
    label: 'Text summary',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
];

function onLogDetail(theLog: LogType) {
  showModalLogDetail.value = true;
  selectedLog.value = theLog;
}

function onLogDetailClose() {
  showModalLogDetail.value = false;
  selectedLog.value = null;
}

function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchLogs();
  }
}

function logLevelVariant(level: string | null | undefined): string {
  if (!level) {
    return 'secondary';
  }

  switch (level) {
    case 'debug':
      return 'info';
    case 'info':
      return 'primary';
    case 'warning':
      return 'warning';
    case 'error':
      return 'danger';
    case 'critical':
      return 'danger';
    default:
      return 'secondary';
  }
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchLogs();
});
</script>

<template>
  <div>
    <h1><font-awesome-icon :icon="['fas', 'file-lines']" /> Audit logs</h1>
    <hr />
    <p>
      Audit logs provide a centralized, real-time view of all critical events
      and system changes across the application. This page enables
      administrators to browse, filter, and analyze audit entries for debugging,
      security investigations, and technical support, ensuring that any
      suspicious activity, errors, or compliance issues are promptly identified
      and addressed.
    </p>
    <!-- Content -->
    <div>
      <!-- Code List Overlay -->
      <b-alert
        v-model="showLogQueryError"
        variant="danger"
        dismissible
        class="mt-3"
      >
        {{ logsQueryError?.message }}
        <b-button
          @click="requestRefetchLogs"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- Filter -->
      <div class="my-3">
        <BForm
          class="d-flex flex-row align-items-end flex-wrap gap-2"
          @submit.prevent="onSearchRequested"
        >
          <BFormGroup label="Organization ID" label-for="organization-id">
            <BFormInput
              id="organization-id"
              type="number"
              v-model.number="organizationId"
              placeholder="Organization ID"
            />
          </BFormGroup>
          <BFormGroup label="User ID" label-for="user-id">
            <BFormInput
              id="user-id"
              type="number"
              v-model.number="userId"
              placeholder="User ID"
            />
          </BFormGroup>
          <BFormGroup label="Action filter" label-for="search-action">
            <BFormInput
              id="search-action"
              type="text"
              v-model="actionPattern"
              placeholder="e.g. project delete"
            />
          </BFormGroup>
          <BFormGroup label="Level filter" label-for="search-level">
            <BFormInput
              id="search-level"
              type="text"
              v-model="levelPattern"
              placeholder="e.g. warning"
            />
          </BFormGroup>
          <BFormGroup label="Search text" label-for="search-text">
            <BFormInput
              id="search-text"
              type="text"
              v-model="textPattern"
              placeholder="e.g. user % details updated"
            />
          </BFormGroup>
          <BFormGroup label="Date from" label-for="date-from">
            <BFormInput id="date-from" type="date" v-model="dateFrom" />
          </BFormGroup>
          <BFormGroup label="Time from" label-for="time-from">
            <BFormInput id="time-from" type="time" v-model="timeFrom" />
          </BFormGroup>
          <BFormGroup label="Date to" label-for="date-to">
            <BFormInput id="date-to" type="date" v-model="dateTo" />
          </BFormGroup>
          <BFormGroup label="Time to" label-for="time-to">
            <BFormInput id="time-to" type="time" v-model="timeTo" />
          </BFormGroup>
          <!-- Filter action buttons -->
          <BButton variant="outline-secondary" @click="clearFilter">
            <font-awesome-icon icon="trash" class="me-1" />
            Clear filter
          </BButton>
          <BButton
            variant="outline-dark"
            type="submit"
            :disabled="logsQueryInProgress"
          >
            <span v-if="logsQueryInProgress">
              <b-spinner small label=""></b-spinner>
              Searching
            </span>
            <span v-else>
              <font-awesome-icon icon="magnifying-glass" class="me-1" />
              Search
            </span>
          </BButton>
        </BForm>
      </div>

      <!-- Paging (above) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />

      <!-- List Logs -->
      <div v-if="!theLogs.length">
        <h5 class="my-5 py-5 text-center">
          There are no logs on this page correspoding to the filter.
        </h5>
      </div>
      <b-card v-else class="my-3">
        <!-- List Logs in Table -->
        <b-table
          striped
          responsive
          hover
          :items="theLogs"
          :fields="fields"
          :busy="logsQueryInProgress"
          id="logs-table"
          class="browser-table"
          @row-clicked="onLogDetail"
        >
          <!-- Log text summary -->
          <template #cell(level)="data">
            <b-badge :variant="logLevelVariant(data.value)">
              {{ data.value }}
            </b-badge>
          </template>
          <!-- Log text summary -->
          <template #cell(text)="data">
            <span :id="'log-text-' + data.item.id">
              {{ truncateText(data.value, TRUNCATE_TEXT_LENGTH) }}
            </span>
            <b-tooltip
              v-if="data.value && data.value.length > TRUNCATE_TEXT_LENGTH"
              :target="'log-text-' + data.item.id"
              class="large-tooltip"
            >
              {{ data.value }}
            </b-tooltip>
          </template>
          <!-- Action buttons -->
          <template #cell(actions)="data">
            <BButtonGroup>
              <BButton
                variant="outline-dark"
                class="btn-sm"
                @click="onLogDetail(data.item)"
              >
                <font-awesome-icon :icon="['fa', 'magnifying-glass']" />
              </BButton>
            </BButtonGroup>
          </template>
        </b-table>
      </b-card>

      <!-- Paging (below) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />

      <!-- Log Detail Modal -->
      <div v-if="selectedLog">
        <BModal
          v-model="showModalLogDetail"
          @close="onLogDetailClose"
          title="Log Detail"
          ok-only
          ok-title="Close"
          size
          class="modal-lg"
        >
          <template #title>
            <BBadge :variant="logLevelVariant(selectedLog.level)">
              {{ selectedLog.level }}
            </BBadge>
            {{ selectedLog.action }}
          </template>

          <!-- Modal Body -->
          <p>
            <strong>Created:</strong>
            {{ formatDateTime(selectedLog.createdAt) }}
            <template v-if="selectedLog.user">
              <br />
              <strong>User:</strong>
              {{ selectedLog.user.givenName }}
              {{ selectedLog.user.familyName }}
              ({{ selectedLog.user.email }})
            </template>
            <template v-if="selectedLog.organization">
              <br />
              <strong>Organization:</strong>
              {{ selectedLog.organization.name }}
            </template>
          </p>
          <template v-if="selectedLog.details">
            <pre
              class="code-highlight"
              v-html="highlightJson(selectedLog.details)"
            ></pre>
          </template>
          <p v-if="selectedLog.text">
            <strong>Text summary</strong><br />
            {{ selectedLog.text }}
          </p>
        </BModal>
      </div>
    </div>
  </div>
</template>
